import React from 'react';
import { debounce } from 'lodash';
import { Button, Col, Collapse, Row } from 'reactstrap';
import Api from '../Api';
import CaretToggle from '../Icons/CaretToggle';
import HtmlHelpers from '../HtmlHelpers';
import ListFunctions from '../FunctionsList';
import WmReactTableServerSide from '../Layout/WmReactTableServerSide';

// =======================================
// Usage

// put `{this.exportTable()}` where you want it to display
// put `exportOpen: true,` in state
// put `exportLoading: true,` in state
// add `this.tableRef = React.createRef();` to constructor

// add the following methods, update storageName and type, set hideBottomHr to true or false

//   updateState = (data) => this.setState(data);
//   toggleExport = () => (
//     this.setState(prevState => (
//       { exportOpen: !prevState.exportOpen }
//     ))
//   )

//   exportTable = () => {
//     const state = this.state;
//     const storageName = 'YOUR_LOCAL_STORAGE_NAME';
//     const options = {
//       auth: this.props.auth,
//       storageName: storageName,
//       data: state.exportData || [],
//       pages: state.exportPages,
//       loading: state.exportLoading,
//       type: 'MODEL_NAME',
//       uuid: state.item.uuid,
//       tableRef: this.tableRef,
//       toggleExport: this.toggleExport,
//       open: state.exportOpen,
//       hideBottomHr: true,
//     }
//     return Exports.getTable(this.updateState, options)
//   }
// =======================================

const Exports = {};
Exports.getData = async (options, updateState) => {
  const filter = {
    pageSize: 100,
    filtered: [{
      id: 'view_model_in',
      value: [options.type]
    }, {
      id: 'view_uuid_in',
      value: [options.uuid]
    }]
  };
  const apiOptions = {
    auth: options.auth,
    listApi: 'exports/exports',
    params: filter,
    tableState: HtmlHelpers.dig([options, 'tableRef', 'current', 'state']),
    storageName: options.storageName
  };
  const data = await ListFunctions.fetchData(apiOptions);
  const exportData = {
    exportData: data.data,
    exportPages: data.pages,
    exportLoading: false
  };
  updateState(exportData);
};
Exports.requeueExport = async (rowId, uuid, options, updateState) => Api.crudUpdate(options.auth, 'exports/exports', {
  uuid
}).then(response => updateState({
  exportData: Exports.setRequeueMsgs(rowId, response, options)
}));
Exports.setRequeueMsgs = (rowId, response, options) => {
  const newData = Object.assign([], options.data);
  if (response.errors === undefined) {
    newData[rowId].requeue_msg = /*#__PURE__*/React.createElement("span", null, "Requeued \xA0");
  } else {
    newData[rowId].requeue_msg = response.errors.base[0];
  }
  newData[rowId].can_requeue = false;
  return newData;
};
Exports.cancelExport = async (rowId, uuid, options, updateState) => Api.crudDelete(options.auth, 'exports/exports', {
  uuid
}).then(response => updateState({
  exportData: Exports.setCancelMsgs(rowId, response, options)
}));
Exports.setCancelMsgs = (rowId, response, options) => {
  const newData = Object.assign([], options.data);
  if (response.errors === undefined) {
    newData[rowId] = response.data.attributes;
  } else {
    newData[rowId].cancel_msg = response.errors.base[0];
  }
  newData[rowId].can_cancel = false;
  return newData;
};
Exports.getTable = (updateState, options) => /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement(Row, null, /*#__PURE__*/React.createElement(Col, {
  md: "6",
  xs: "12"
}, /*#__PURE__*/React.createElement("h4", null, "Export History (", options.loading ? 'loading' : options.data.length, ") \xA0\xA0\xA0", /*#__PURE__*/React.createElement(CaretToggle, {
  onClick: options.toggle,
  open: options.open
}))), /*#__PURE__*/React.createElement(Col, {
  md: "6",
  xs: "12",
  className: "text-right"
}, options.titleRight)), options.hideBottomHr ? '' : /*#__PURE__*/React.createElement("hr", null), /*#__PURE__*/React.createElement(Collapse, {
  isOpen: options.open
}, /*#__PURE__*/React.createElement(WmReactTableServerSide, {
  ref: options.tableRef,
  className: "otto__short_table",
  pages: options.pages,
  data: options.data,
  loading: options.loading,
  columns: Exports.getColumns(options, updateState),
  onFetchData: debounce(() => Exports.getData(options, updateState), 500)
})));
const baseLink = "".concat(process.env.REACT_APP_BASE_URL, "/exports").concat(process.env.REACT_APP_URL_ENV, "/view/");
const requeueBtn = (data, onClick) => data.can_requeue !== true ? data.requeue_msg || '' : /*#__PURE__*/React.createElement(Button, {
  outline: true,
  color: "secondary",
  size: "sm",
  onClick: onClick
}, /*#__PURE__*/React.createElement("span", {
  className: "small"
}, "Requeue"));
const cancelBtn = (data, onClick) => data.can_cancel !== true ? data.cancel_msg || '' : /*#__PURE__*/React.createElement(Button, {
  outline: true,
  color: "secondary",
  size: "sm",
  onClick: onClick
}, /*#__PURE__*/React.createElement("span", {
  className: "small"
}, "Cancel"));
Exports.getColumns = (options, updateState) => [{
  id: 'id',
  Header: 'ID',
  accessor: d => /*#__PURE__*/React.createElement("a", {
    href: "".concat(baseLink).concat(d.uuid)
  }, d.id),
  className: 'text-center',
  width: 75
}, {
  Header: 'Attached To',
  accessor: 'attached_to',
  className: 'text-left',
  width: 145
}, {
  Header: 'Service Type',
  accessor: 'service_type',
  className: 'text-left',
  width: 250
}, {
  Header: 'Description',
  accessor: 'description',
  className: 'text-left'
}, {
  Header: 'Last Error',
  accessor: 'last_error',
  className: 'text-left',
  width: 200,
  filterable: false,
  sortable: false
}, {
  id: 'started_attempt_at',
  Header: 'Started At',
  accessor: d => HtmlHelpers.format_utc_date_time(d.started_attempt_at),
  className: 'text-right',
  width: 100
}, {
  id: 'exported_at',
  Header: 'Exported At',
  accessor: d => HtmlHelpers.format_utc_date_time(d.exported_at),
  className: 'text-right',
  width: 100
}, {
  id: 'confirmed_at',
  Header: 'Confirmed At',
  accessor: d => HtmlHelpers.format_utc_date_time(d.confirmed_at),
  className: 'text-right',
  width: 100
}, {
  id: 'canceled_at',
  Header: 'Canceled At',
  accessor: d => HtmlHelpers.format_utc_date_time(d.canceled_at),
  className: 'text-right',
  width: 100
}, {
  Header: 'Attempts',
  accessor: 'attempts',
  className: 'text-center',
  width: 75
}, {
  id: 'created_at',
  Header: 'Created At',
  accessor: d => HtmlHelpers.format_utc_date_time(d.created_at),
  className: 'text-right',
  width: 100
}, {
  id: 'updated_at',
  Header: 'Updated At',
  accessor: d => HtmlHelpers.format_utc_date_time(d.updated_at),
  className: 'text-right',
  width: 100
}, {
  id: 'requeue',
  Header: '',
  accessor: 'can_requeue',
  Cell: row => {
    const requeueOnClick = () => Exports.requeueExport(row.index, row.original.uuid, options, updateState);
    const cancelOnClick = () => Exports.cancelExport(row.index, row.original.uuid, options, updateState);
    return /*#__PURE__*/React.createElement("div", {
      className: "text-center"
    }, requeueBtn(row.original, requeueOnClick), "\xA0\xA0\xA0\xA0", cancelBtn(row.original, cancelOnClick));
  },
  className: 'text-center',
  width: 180
}];
export default Exports;